import type { FormAutocompleteDropdownProps } from 'app/src/model'
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteProps } from 'primereact/autocomplete'
import { FloatLabel } from 'primereact/floatlabel'
import { SelectItem } from 'primereact/selectitem'
import { useEffect, useState } from 'react'

export function AutocompleteDropdown(props: FormAutocompleteDropdownProps) {
  const { labelProps, options = [], onChange, ...dropdownProps } = props
  const [items, setItems] = useState<SelectItem[]>([])
  const [selectedOption, setSelectedOption] = useState<SelectItem | string>()

  const [typedValue, setTypedValue] = useState<string>()

  // Sync input text with external value
  useEffect(() => {
    const match = options.find((o) => (o as SelectItem).value === props.value) as SelectItem | undefined

    if (match || !props.value) {
      setTypedValue(undefined)
    }
    setSelectedOption(match)
  }, [props.value, options])

  // Normalize event and emit to parent
  const emitChange = (e: AutoCompleteChangeEvent, val?: string) => {
    if (!e?.value?.value || e?.value?.value !== props.value) {
      const value = val ?? e.value?.value ?? e.value
      const event = {
        ...e,
        target: { ...e.target, value },
        value,
      }
      onChange?.(event)
    }
  }

  const search = (query?: string) => {
    const _items = props.options as SelectItem[]
    setItems(
      query && query !== '' ? _items.filter((item) => item.label?.toLowerCase().startsWith(query.toLowerCase())) : [..._items],
    )
  }

  return (
    <>
      <FloatLabel>
        <AutoComplete
          inputId={labelProps.text}
          inputClassName='shadow-none rounded-none border-none !font-normal !text-black'
          field='label'
          pt={{
            root: {
              className:
                'focus-within:shadow-[0_0_0_0.2rem_#0047bb] transition outline-none offset-0 rounded-none !border-0 !border-b border-[#D9D9D9]',
            },
            input: {
              root: {
                'data-testid': labelProps.testId,
              },
            },
            dropdownButton: {
              root: {
                tabIndex: -1,
                className: 'bg-white text-[var(--primary-color)] border-none shadow-none',
              },
            },
          }}
          {...(dropdownProps as AutoCompleteProps)}
          forceSelection={true}
          delay={0}
          value={typedValue ?? selectedOption}
          onChange={(e) => {
            const type = e?.originalEvent?.type
            if (type === 'change') {
              setTypedValue(e.value)
              // If user accidentally types a valid code, do not emit
              const valueMatchesOptionValue = props.options.find((o) => (o as SelectItem).value === e.value)
              if (valueMatchesOptionValue) return
            }
            if (type === 'blur' && e.value === null) {
              return
            }
            emitChange(e)
          }}
          suggestions={items}
          completeMethod={(e) => search(e.query)}
          onDropdownClick={() => search(typedValue)}
          dropdown
        />
        <label htmlFor={labelProps.text}>{labelProps.text}</label>
      </FloatLabel>
    </>
  )
}
